import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { MessageCircle, X, Send, Bot, User as UserIcon, LifeBuoy, Loader2 } from 'lucide-react';

/**
 * Floating chat widget — auto-picks persona from user.role.
 * - customer  → "Bazaar Buddy"
 * - owner     → "Dukandaar Sahayak"
 * - admin     → "Operations Copilot"
 */
export default function ChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const persona = user?.role === 'admin' ? 'admin' : user?.role === 'owner' ? 'owner' : 'customer';
  const TITLE = { customer: 'Bazaar Buddy', owner: 'Dukandaar Sahayak', admin: 'Operations Copilot' };

  useEffect(() => {
    if (messages.length === 0 && open && user) {
      const greet = persona === 'customer'
        ? 'Namaste! How can I help you today? 👋\nYou can ask about orders, refunds, voice ordering or how BharatCart works.'
        : persona === 'owner'
        ? 'Namaste dukandaar! How can I help? You can ask about adding products, KYC, khata book, business stats or live tenders.'
        : 'Welcome, Admin. Ask me about customer trends, KYC pending owners, broadcast drafts, or summarise this week\'s revenue.';
      setMessages([{ role: 'assistant', text: greet }]);
    }
  }, [open, user, persona]); // eslint-disable-line

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!user) { toast.info('Please log in to chat.'); return; }
    const text = input.trim();
    if (!text) return;
    setMessages(m => [...m, { role: 'user', text }]);
    setInput('');
    setBusy(true);
    try {
      const r = await api.post('/chat/message', {
        persona,
        session_id: sessionId,
        message: text,
        context: { page: window.location.pathname },
      });
      setSessionId(r.data.session_id);
      setMessages(m => [...m, { role: 'assistant', text: r.data.reply }]);
    } catch (e) {
      setMessages(m => [...m, { role: 'assistant', text: 'Sorry, something went wrong. Please try again or type "talk to human".' }]);
    } finally { setBusy(false); }
  };

  const escalate = async () => {
    if (!sessionId) return toast.info('Send a message first so we have context.');
    try {
      const r = await api.post('/chat/escalate', { session_id: sessionId, subject: messages.slice(-1)[0]?.text?.slice(0, 100) || 'Need human help', body: '' });
      toast.success(`Ticket #${r.data.id.slice(0, 6)} created. Our team will respond shortly.`);
      setMessages(m => [...m, { role: 'assistant', text: `✓ Escalated to a human. Ticket ID: ${r.data.id.slice(0, 8)}. We will reach out via your phone ${user.phone}.` }]);
    } catch (e) { toast.error('Could not escalate'); }
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          data-testid="chat-fab"
          onClick={() => setOpen(true)}
          className="fixed bottom-36 md:bottom-24 right-4 md:right-5 z-[60] inline-flex items-center gap-2 rounded-full bg-[#D35400] hover:bg-[#B84300] text-white shadow-lg px-4 py-3 text-sm font-semibold animate-pulse-slow"
        >
          <MessageCircle className="w-5 h-5"/>
          <span className="hidden sm:inline">{TITLE[persona]}</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div data-testid="chat-panel" className="fixed bottom-20 md:bottom-24 right-4 z-[60] w-[360px] max-w-[92vw] h-[520px] max-h-[80vh] rounded-2xl border border-border bg-white shadow-2xl flex flex-col overflow-hidden">
          <div className="flex items-center gap-2 bg-gradient-to-r from-[#D35400] to-[#E67E22] text-white px-4 py-3">
            <Bot className="w-5 h-5"/>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-sm leading-tight">{TITLE[persona]}</div>
              <div className="text-[11px] opacity-90">Powered by GPT-5.2 · Free for you</div>
            </div>
            <button data-testid="chat-escalate" onClick={escalate} title="Talk to a human" className="p-1.5 rounded-md hover:bg-white/20"><LifeBuoy className="w-4 h-4"/></button>
            <button data-testid="chat-close" onClick={() => setOpen(false)} className="p-1.5 rounded-md hover:bg-white/20"><X className="w-4 h-4"/></button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-[#FBF8F1]">
            {messages.map((m, i) => (
              <div key={`${m.role}-${i}`} data-testid={`chat-msg-${i}`} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {m.role === 'assistant' && <div className="w-7 h-7 rounded-full bg-[#FFF3E0] text-[#D35400] grid place-items-center shrink-0"><Bot className="w-4 h-4"/></div>}
                <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-[#D35400] text-white rounded-br-sm' : 'bg-white border border-border rounded-bl-sm'}`}>{m.text}</div>
                {m.role === 'user' && <div className="w-7 h-7 rounded-full bg-[#0D47A1] text-white grid place-items-center shrink-0"><UserIcon className="w-4 h-4"/></div>}
              </div>
            ))}
            {busy && <div className="flex items-center gap-2 text-xs text-ink/50"><Loader2 className="w-3 h-3 animate-spin"/>Thinking…</div>}
          </div>

          <div className="border-t border-border p-2 flex items-center gap-2 bg-white">
            <input
              data-testid="chat-input"
              value={input}
              onChange={e=>setInput(e.target.value)}
              onKeyDown={e=>{ if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={user ? 'Type your question…' : 'Sign in to chat'}
              className="flex-1 rounded-full border border-border px-3 py-2 text-sm outline-none focus:border-[#E67E22] focus:ring-2 focus:ring-[#E67E22]/20"
              disabled={!user || busy}
            />
            <button data-testid="chat-send" onClick={send} disabled={!input.trim() || busy} className="rounded-full bg-[#D35400] disabled:opacity-40 text-white p-2"><Send className="w-4 h-4"/></button>
          </div>
        </div>
      )}
      <style>{`@keyframes pulseSlow{0%,100%{box-shadow:0 0 0 0 rgba(211,84,0,0.4)}50%{box-shadow:0 0 0 12px rgba(211,84,0,0)}}.animate-pulse-slow{animation:pulseSlow 2.4s ease-in-out infinite}`}</style>
    </>
  );
}
